'use client'

import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { Fragment, useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

type ActionProps = {
  name: string
  total: number
  searchParams: any
}

export default function Action({ name, total, searchParams }: ActionProps) {
  const [viewGrid, setViewGrid] = useState(!searchParams.view_ga || searchParams.view_ga === 'true')
  const [numberView, setNumberView] = useState(searchParams.number_view || 40)
  const [orderSort, setOrderSort] = useState(searchParams.order_sort || '3')

  const t = useTranslations('product')
  const router = useRouter()
  const pathname = usePathname()

  const handleSelect = (key: 'view_ga' | 'number_view' | 'order_sort', value: string | number) => {
    if (key === 'view_ga') {
      setViewGrid(String(value) === 'true')
    }

    const params = new URLSearchParams(searchParams)
    params.set(key, String(value))
    params.delete('page')
    router.push(`${pathname}?${params.toString()}`, { scroll: true })
  }

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>, type: 'number_view' | 'order_sort') => {
    const value = e.target.value
    if (type === 'number_view') {
      setNumberView(Number(value))
    } else {
      setOrderSort(value)
    }
    handleSelect(type, value)
  }

  useEffect(() => {
    const view = searchParams.view_ga
    const number = searchParams.number_view
    const order = searchParams.order_sort

    if (view) {
      setViewGrid(JSON.parse(view))
    }

    if (number) {
      setNumberView(Number(number))
    }

    if (order) {
      setOrderSort(order)
    }
  }, [searchParams])

  return (
    <div className="hidden lg:block">
      <div className="mb-4 flex justify-between items-center">
        <div className="text-xl text-thegioiic-gray flex-auto">
          {name} {total > 0 && <span className="text-primary">({total})</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded overflow-hidden border border-gray-500 flex items-center">
            <div className="p-1 grid grid-cols-2 gap-0.5 border-r border-r-gray-500 cursor-pointer hover:opacity-70" onClick={() => handleSelect('view_ga', JSON.stringify(true))}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={`grid-${index}`}
                  className={cn('w-2.5 h-2.5 rounded-[2px] border border-transparent', {
                    'bg-green-700': viewGrid,
                    'bg-white border-gray-500': !viewGrid,
                  })}
                ></div>
              ))}
            </div>
            <div className="p-1 grid grid-cols-3 gap-0.5 cursor-pointer hover:opacity-70" onClick={() => handleSelect('view_ga', JSON.stringify(false))}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Fragment key={`list-${index}`}>
                  <div
                    className={cn('col-span-1 h-[6px] w-2 rounded-[2px] border border-transparent', {
                      'bg-green-700': !viewGrid,
                      'bg-white border-gray-500': viewGrid,
                    })}
                  ></div>
                  <div
                    className={cn('col-span-2 h-[6px] rounded-[2px] border border-transparent', {
                      'bg-green-700': !viewGrid,
                      'bg-white border-gray-500': viewGrid,
                    })}
                  ></div>
                </Fragment>
              ))}
            </div>
          </div>
          <select
            value={numberView}
            onChange={(e) => handleChangeSelect(e, 'number_view')}
            className="hover:border-primary border border-bordercolor text-sm py-1 rounded px-[5px] cursor-pointer"
          >
            {[40, 80, 120, 160, 200].map((item) => (
              <option key={item} value={item}>
                {t('limit')} {item}
              </option>
            ))}
          </select>
          <select
            value={orderSort}
            onChange={(e) => handleChangeSelect(e, 'order_sort')}
            className="hover:border-primary border border-bordercolor text-sm py-1 rounded px-[5px] cursor-pointer"
          >
            <option value="3">{t('inventory')}</option>
            <option value="1">{t('name')}</option>
            <option value="2">{t('price')}</option>
            <option value="4">{t('newest')}</option>
            <option value="5">{t('default')}</option>
          </select>
        </div>
      </div>
    </div>
  )
}
